import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FilterCollection } from "../../../services/FilterService";
import InvoiceService from "../../../services/InvoiceService";
import AdvancedFilters, {
  AdvancedFiltersProvider
} from "../../shared/components/AdvancedFilters";
import Table, { TableHeaderWithFieldId } from "../../shared/Table";
import InvoiceTableHeader from "./InvoiceTableHeader";
import InvoiceTableItem from "./InvoiceTableItemConsolidated";
import InvoiceTableProvider from "./InvoiceTableProvider";
import InvoiceTableTotal from "./InvoiceTableTotal";
import TableContext from "../../task/TableContext";
import { DataType } from "../../../services/CompanyService";
import { InvoiceGroupContext } from "./providers/InvoiceTableProvider";
import { FixToTop } from "../../shared/FixToTop";
import { TranslationService } from "../../../services/TranslationService";

type InvoiceListType = {
  client: {
    PersonId: number;
    Name: string;
  };
  consolidatedPk: string;
  groupId?: number;
  setReloads?: { invoice: () => void };
  reload: () => void;
};

const InvoiceList = ({
  client,
  consolidatedPk,
  groupId,
  setReloads = undefined,
  reload
}: InvoiceListType) => {
  const { ioTree } = useContext(InvoiceGroupContext);
  const [isExpandedOpen, setIsExpandedOpen] = useState(false);

  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const scrollTopRef = useRef<number | null>(null);

  useEffect(() => {
    if (scrollContainerRef.current && scrollTopRef.current !== null) {
      // El body scrollable del Table ahora usa overflow-y-auto
      const scrollable = scrollContainerRef.current.querySelector(
        ".tableContainer.overflow-y-auto.overflow-x-auto"
      ) as HTMLDivElement;
      if (scrollable) {
        scrollable.scrollTop = scrollTopRef.current;
        scrollTopRef.current = null;
      }
    }
  }, []);

  useEffect(() => {
    if (!isExpandedOpen) {
      return;
    }
    const scrollY = window.scrollY;
    document.body.style.overflow = "hidden";
    document.body.style.position = "fixed";
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = "100%";

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsExpandedOpen(false);
      }
    };
    document.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.width = "";
      window.scrollTo(0, scrollY);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [isExpandedOpen]);

  //const showToggleCheckbox = CompanyService.canDo("editio") ? toggleOneCheckbox : undefined;

  const tableHeaders = useMemo(() => {
    // Consolidado: solo campos fijos (sin adicionales)
    return [
      new TableHeaderWithFieldId(
        "Person.name",
        TranslationService.translate.Company,
        false,
        false,
        "Name",
        DataType.Text,
        "long-text th-min-200"
      ),
      // Nota: la key de traducción en el proyecto es "EmmitedDate" (typo histórico)
      new TableHeaderWithFieldId(
        "emittedDate",
        TranslationService.translate.EmmitedDate,
        false,
        false,
        "emittedDate",
        DataType.Date,
        "th-w-160"
      ),
      new TableHeaderWithFieldId(
        "referenceNumber",
        InvoiceService.getReferenceNumberName(),
        false,
        false,
        "referenceNumber",
        DataType.Text,
        "th-w-160"
      ),
      new TableHeaderWithFieldId(
        "dueDate",
        TranslationService.translate.DueDate,
        false,
        false,
        "dueDate",
        DataType.Date,
        "th-w-160"
      ),
      new TableHeaderWithFieldId(
        "amount",
        TranslationService.translate.Amount,
        true,
        true,
        "amount",
        DataType.Currency,
        "th-w-160"
      ),
      new TableHeaderWithFieldId(
        "pendingamount",
        TranslationService.translate.PendingAmount,
        true,
        true,
        "pending",
        DataType.Currency,
        "th-w-160"
      )
    ];
  }, []);

  return (
    <InvoiceTableProvider
      clientId={client.PersonId}
      groupId={groupId}
      consolidatedPk={consolidatedPk}
    >
      <AdvancedFiltersProvider>
        <TableContext.Consumer>
          {({ reload }) => {
            if (setReloads) {
              setReloads.invoice = reload;
            }
            return <></>;
          }}
        </TableContext.Consumer>
        <div className="backPublicSmooth pb-3">
          <div className="pt-3 client-detail-section-title">
            {TranslationService.translate.Invoices}
          </div>
        </div>
        <span id="invoice-header-marker" />

        <FixToTop id="fixTopInvoices">
          <InvoiceTableHeader
            client={client}
            ioTreeFilters={ioTree}
            reload={() =>
              setReloads ? Object.values(setReloads).map((x) => x()) : undefined
            }
            consolidated
            onExpand={() => setIsExpandedOpen(true)}
          />
          <div className="mx-0">
            <AdvancedFilters page={FilterCollection.Invoice} />
          </div>
        </FixToTop>

        <div ref={scrollContainerRef} className="invoice-list-container">
          <Table
            headers={tableHeaders}
            item={InvoiceTableItem(undefined, tableHeaders, reload)}
            stickyHeader={true}
          >
            <InvoiceTableTotal tableHeaders={tableHeaders} />
          </Table>
        </div>

        {isExpandedOpen &&
          createPortal(
            <div
              className="invoice-expanded-overlay"
              onClick={() => setIsExpandedOpen(false)}
            >
              <div
                className="invoice-expanded-dialog"
                onClick={(e) => e.stopPropagation()}
              >
                <InvoiceTableHeader
                  client={client}
                  ioTreeFilters={ioTree}
                  reload={() =>
                    setReloads
                      ? Object.values(setReloads).map((x) => x())
                      : undefined
                  }
                  consolidated
                  onExpand={() => setIsExpandedOpen(true)}
                  isExpandedView
                  onClose={() => setIsExpandedOpen(false)}
                />
                <div className="invoice-expanded-body">
                  <div className="mx-0">
                    <AdvancedFilters page={FilterCollection.Invoice} />
                  </div>
                  <Table
                    headers={tableHeaders}
                    item={InvoiceTableItem(undefined, tableHeaders, reload)}
                    stickyHeader={true}
                    additionalClass="invoice-expanded-tbody"
                  >
                    <InvoiceTableTotal tableHeaders={tableHeaders} />
                  </Table>
                </div>
              </div>
            </div>,
            document.body
          )}
      </AdvancedFiltersProvider>
    </InvoiceTableProvider>
  );
};

export default InvoiceList;
